import type { EmailDeliveryStatusKey } from '~/hooks/apis/customer-communication/useEmailDeliveryStatus';

export interface InstructionBody {
  type?: EmailDeliveryStatusKey;
  title: string;
  instructions: string[];
  removeButtonLabel?: string;
  infoOnly?: boolean;
}

export const SUPPRESSION_TYPES: EmailDeliveryStatusKey[] = ['hardBounce', 'spamMarked', 'unsubscribed'];

export const UNKNOWN_INSTRUCTION: InstructionBody = {
  title: 'Some statuses could not be retrieved',
  infoOnly: true,
  instructions: [
    '"Unknown" does not mean the customer is clear — it means we couldn\'t confirm the status due to a temporary issue with our email provider.',
    'Try refreshing using the button above.',
    'If the problem persists, please contact engineering support.',
  ],
};

export const STATUS_CONFIG: Record<
  EmailDeliveryStatusKey,
  {
    category: string;
    activeLabel: string;
    clearLabel: string;
    instruction: InstructionBody;
  }
> = {
  hardBounce: {
    category: 'Hardbounce status',
    activeLabel: 'Hard bounced',
    clearLabel: 'Clear',
    instruction: {
      type: 'hardBounce',
      title: 'How to resolve: Hard bounced',
      removeButtonLabel: 'Remove hard bounce',
      instructions: [
        "Confirm the customer's email address is correct.",
        'If the email is incorrect: confirm the correct email address with the customer, then update it in the User Details tab before taking any further action.',
        'If the email is correct: ask the customer to whitelist m.luxuryescapes.com with their email provider.',
        'Confirm whitelisting has been completed by the customer.',
        'Once whitelisting is confirmed and the root cause is resolved, remove the customer from the Hard Bounce suppression list.',
      ],
    },
  },
  spamMarked: {
    category: 'Spam status',
    activeLabel: 'Marked as spam',
    clearLabel: 'Clear',
    instruction: {
      type: 'spamMarked',
      title: 'How to resolve: Marked as spam',
      removeButtonLabel: 'Remove spam block',
      instructions: [
        'Ask the customer to whitelist m.luxuryescapes.com with their email provider.',
        'Ask the customer to move any of our emails from their spam/junk folder to their inbox and remove the spam flag.',
        'Confirm whitelisting has been completed by the customer.',
        'Once whitelisting is confirmed, remove the customer from the Spam suppression list.',
      ],
    },
  },
  unsubscribed: {
    category: 'Subscription',
    activeLabel: 'Unsubscribed',
    clearLabel: 'Subscribed',
    instruction: {
      type: 'unsubscribed',
      title: 'Unsubscribed from marketing emails',
      infoOnly: true,
      instructions: [
        'Note: this cannot be changed directly from Admin.',
        'Ask the customer to re-subscribe via Communication Preferences in their account settings.',
        'If the customer provides verbal or written consent during the interaction, re-subscribe them on their behalf.',
        "Once re-subscribed (self-serve or agent-assisted), verify the customer's subscription status in Admin before ending the interaction.",
      ],
    },
  },
};

export function getInstructionBody(
  key: EmailDeliveryStatusKey,
  deliveryStatus: Record<EmailDeliveryStatusKey, { status: boolean | null }>,
): InstructionBody {
  if (deliveryStatus[key].status === null) return UNKNOWN_INSTRUCTION;
  return STATUS_CONFIG[key].instruction;
}
